import { useEffect, useState, type FormEvent } from "react";
import {
  Button,
  Divider,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBrandApple, IconBrandGoogle, IconMail } from "@tabler/icons-react";
import { useAuth } from "../lib/authContext";

type SignInModalProps = {
  opened: boolean;
  onClose: () => void;
};

export function SignInModal({ opened, onClose }: SignInModalProps) {
  const { signInWithGoogle, sendEmailSignInLink } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    if (opened) return;
    setEmail("");
    setSentTo(null);
    setSubmitting(false);
    setSigningIn(false);
  }, [opened]);

  const handleGoogle = () => {
    setSigningIn(true);
    signInWithGoogle()
      .then(() => onClose())
      .catch(() => {
        notifications.show({
          color: "red",
          title: "Sign-in failed",
          message: "Couldn't sign you in. Please try again.",
        });
      })
      .finally(() => setSigningIn(false));
  };

  const handleApple = () => {
    notifications.show({
      color: "yellow",
      title: "About that…",
      message: "Apple makes me pay $99 a year to enable this. No.",
      autoClose: 6000,
    });
  };

  const handleEmail = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await sendEmailSignInLink(trimmed);
      setSentTo(trimmed);
    } catch {
      notifications.show({
        color: "red",
        title: "Couldn't send link",
        message: "Check the address and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Sign in to nifusion.dev"
      centered
      size="sm"
    >
      {sentTo ? (
        <Stack gap="sm">
          <Text size="sm">
            Sent a sign-in link to <strong>{sentTo}</strong>.
          </Text>
          <Text size="sm" c="dimmed">
            Click the link in the email to finish signing in. You can close
            this window; the link will sign you in when you click it.
          </Text>
          <Button variant="default" onClick={onClose} fullWidth>
            Got it
          </Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <Button
            leftSection={<IconBrandGoogle size={16} />}
            onClick={handleGoogle}
            loading={signingIn}
            disabled={signingIn || submitting}
            fullWidth
          >
            Continue with Google
          </Button>

          <Button
            leftSection={<IconBrandApple size={16} />}
            onClick={handleApple}
            disabled={signingIn || submitting}
            variant="default"
            fullWidth
          >
            <span className="line-through">Continue with Apple</span>
          </Button>

          <Divider label="or" labelPosition="center" />

          <form onSubmit={handleEmail}>
            <Stack gap="sm">
              <TextInput
                type="email"
                label="Email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                autoFocus
                data-autofocus
              />
              <Button
                type="submit"
                leftSection={<IconMail size={16} />}
                loading={submitting}
                variant="default"
                fullWidth
              >
                Send sign-in link
              </Button>
              <Text size="xs" c="dimmed">
                No password needed. We'll email you a link that signs you in.
              </Text>
            </Stack>
          </form>
        </Stack>
      )}
    </Modal>
  );
}
